/**
 * ★ **재생용 `<video>` 한 쌍의 소유자** (v19).
 *
 * ── 왜 앱이 소유하는가 ───────────────────────────────────────
 *
 * **WebKit 은 소리 있는 재생 허가를 엘리먼트 단위로 기억한다.** 채널을 바꾸면
 * `ChannelGate` 가 `key` 로 `WatchScreen` 을 재마운트하고 어댑터가 `<video>` 를 새로
 * 만드는데, 그 새 엘리먼트에는 허가가 없고 전환의 `start()` 는 `useEffect` 안이라
 * **제스처 밖**이다 — 아이폰에서 `PLAYBACK_FAILED` 로 나타났고, v12 는 그 자리에
 * 게이트를 한 번 더 세워 막았다(`TASKS-V12` 의 그 표).
 *
 * **한 쌍을 앱이 들고 있으면 그 조건이 사라진다.** 자연 전환·스킵·선택이 아이폰에서
 * 멀쩡한 이유가 곧 근거다 — **같은 엘리먼트를 계속 쓰기 때문**이고, 그 셋은 제스처
 * 밖에서 `play()` 를 부르는데도 된다.
 *
 * ── 왜 모듈 스코프인가 ───────────────────────────────────────
 *
 * 컴포넌트가 들면 그 컴포넌트의 수명을 탄다. 살아남아야 하는 것이 정확히 **재마운트를
 * 건너서**이므로 트리 밖에 둔다 — `key` 도 `switching` 의 빈 커밋도 그대로 두고
 * 엘리먼트만 다음 컨테이너로 옮겨 간다(`appendChild` 가 옮겨 준다).
 *
 * **스타일은 어댑터가 정한다.** 여기서는 만들기만 하고, `createBuffer` 가 `playsinline`
 * 부터 `opacity` 까지 전부 다시 세운다 — 두 곳에서 정하면 어긋난다.
 */
let pair: readonly [HTMLVideoElement, HTMLVideoElement] | null = null;

/**
 * **만들자마자 문서에 붙인다** (v19). 붙이는 곳은 화면 밖의 보관함이고, 어댑터가
 * 입양하면서 무대로 옮겨 간다.
 *
 * **DOM 안에 있어야 하는 이유는 `unlockBuffers()` 다** — 제스처 중의 `play()` 로 허가를
 * 심으려면 엘리먼트가 문서에 있어야 한다는 것이 `TASKS-V12` 가 적어 둔 미검증 전제 둘 중
 * 하나였다. **그 하나는 이렇게 없앤다.**
 *
 * `display:none` 이 아니다 — 그것은 미디어를 아예 못 돌게 할 수 있다. 크기 0 으로 숨긴다.
 */
function host(): HTMLElement {
  const found = document.getElementById('bloom-buffers');
  if (found !== null) return found;
  const el = document.createElement('div');
  el.id = 'bloom-buffers';
  el.style.cssText = 'position:fixed;width:0;height:0;overflow:hidden;pointer-events:none;';
  document.body.appendChild(el);
  return el;
}

export function playbackBuffers(): readonly [HTMLVideoElement, HTMLVideoElement] {
  if (pair === null) {
    const made: [HTMLVideoElement, HTMLVideoElement] = [
      document.createElement('video'),
      document.createElement('video'),
    ];
    for (const el of made) {
      el.playsInline = true;
      el.setAttribute('webkit-playsinline', 'true');
      host().appendChild(el);
    }
    pair = made;
  }
  return pair;
}

/**
 * ★ **제스처 안에서 허가를 심는다** (v19).
 *
 * ── 어디에 쓰나 ──────────────────────────────────────────────
 *
 * 알림·QR·딥링크로 들어온 상품 화면은 **전체 이동**이라(`openFromNotification`) 문서가
 * 새것이고, 그래서 위 한 쌍도 허가가 없다. 거기서 `[방송 보기]` 를 누르면 이동은
 * `pushState`(같은 문서)인데 `start()` 는 그 뒤 `useEffect` 라 **제스처 밖**이다 —
 * v12 가 채널 전환에서 만난 것과 같은 모양이고, 그래서 게이트가 한 번 더 떴다.
 *
 * **클릭 그 자체는 제스처다.** WebKit 은 제스처 중의 `play()` 에서 엘리먼트의 재생 제약을
 * 푸는 것으로 알려져 있으므로, 그 순간에 한 번 불러 둔다.
 *
 * ── ★ 전제는 닫혔다 ─────────────────────────────────────────
 *
 * **「소스가 없는 엘리먼트에도 제스처 중의 `play()` 로 허가가 심힌다」** — `TASKS-V12` 가
 * 「엘리먼트 주입」을 버릴 때 적은 두 전제 중 남아 있던 것이고, **v19 가 실기로 닫았다**
 * (2026-08-20 · `docs/39` §19.7 — 아이폰·Android 두 대에서 알림 → 상품 화면 →
 * `[방송 보기]` 가 **게이트 없이** 재생됐다). 다른 하나(「DOM 밖 엘리먼트」)는 위
 * `host()` 가 애초에 성립하지 않게 한다.
 *
 * **v25 가 그것을 한 자리 더 확인했다** (2026-08-31) — 진입 게이트의 편성표에서 다른
 * 채널의 회차를 고르면 `start()` 는 새 화면의 `useEffect` 안이라 **제스처 밖**인데,
 * 아이폰에서 **오류 없이 바로 재생됐다.**
 *
 * > ★ **이 문단이 「확인된 바 없다」로 남아 있던 것이 v25 에서 값을 치렀다.** 그 한 줄을
 * > 믿고 **고른 것을 게이트 버튼까지 미루는 구조**를 한 번 만들었고, 실기로 확인하고서야
 * > 걷어냈다. **닫힌 전제는 닫혔다고 적어야 한다** — 안 적으면 다음 사람이 없는 위험을
 * > 피하느라 화면을 한 겹 더 만든다.
 *
 * **그래도 틀렸을 때의 증상은 알아 둔다** — 「게이트 대신 오류 화면」이고 `다시 시도`
 * 한 번으로 재생된다. 되돌리기는 호출 한 줄을 지우는 것이다.
 */
export function unlockBuffers(): void {
  if (unlocked) return;
  for (const el of playbackBuffers()) {
    // **소리 있는 재생의 허가**를 노린다 — 음소거 재생은 원래 허용이라 심을 것이 없다.
    el.muted = false;
    /*
     * ★★★ **같은 틱에 되돌린다 — `.then()` 으로 미루지 말 것** (2026-09-22 무대 실기).
     *
     * 소스가 없는 엘리먼트의 `play()` 는 **지금 풀리지 않는다.** 누군가 이 버퍼에 스트림을
     * 붙여 **실제로 재생이 시작되는 그 순간** 비로소 풀린다 — 그래서 `.then(() => pause())`
     * 는 「내가 건 재생을 되돌리는」 것이 아니라 **남이 시작한 재생을 멈추는** 것이 된다.
     * 미뤄 둔 한 줄이 수백 밀리초 뒤에 남의 손을 잡는다.
     *
     * **녹화 채널에서는 증상이 없었다** — 리시버의 `play()` 가 그 `pause()` **뒤에** 와서
     * 곧바로 되살린다(실측: `7090 pause` → `7091 play` → `7092 playing`). 라이브 이벤트
     * 창에 들어가면 순서가 뒤집힌다 — `enterLiveNow` 의 `play()` 가 `loadeddata` 와 같은
     * 틱이라 **먼저** 오고, 남은 `pause()` 가 **뒤에** 와서 이긴다. 화면은 🔴 LIVE 배지를
     * 단 채 첫 프레임에서 굳고 오류는 한 줄도 없다.
     *
     * **허가는 부른 순간에 심긴다 — 멈춘다고 풀리지 않는다.** 같은 실측이 그것을 보여
     * 준다: 7090 에 멈춘 엘리먼트가 **제스처 밖인 7091 의 `play()`** 로 되살아났다.
     *
     * 뒤 버퍼를 위해서도 이래야 한다. 재생 대기 상태로 두면 `prepareNext` 가 소스를 붙이는
     * 순간 **다음 회차가 조용히 흘러가** 꼬리 스왑이 엉뚱한 자리에서 터진다.
     */
    void Promise.resolve(el.play()).catch(() => undefined);
    el.pause();
    el.muted = true;
  }
  unlocked = true;
}

/**
 * ★ **이 문서에서 한 번이라도 소리 있는 재생이 성립했는가** (v19).
 *
 * 게이트는 자동재생 정책 때문에 있고, 그 정책이 요구하는 것은 **엘리먼트가 허가를 얻는
 * 첫 한 번**이다. 위 한 쌍을 계속 쓰므로 **그 한 번이 지나면 다시 필요하지 않다** —
 * 채널 전환도, 상품 화면에서 `[방송 보기]` 로 돌아오는 것도 같은 문서·같은 엘리먼트다.
 *
 * **첫 진입에서는 여전히 필요하다.** 그래서 「기기가 무엇인가」가 아니라 **「이미 허가를
 * 얻었는가」**로 묻는다 — 새 문서(새로고침·앱 재시작)에서는 이 값이 거짓으로 돌아가고
 * 게이트가 제자리에 선다.
 *
 * **재생이 실제로 붙었을 때만 참으로 만든다.** 「클릭했다」가 아니라 「소리 있는 재생이
 * 성립했다」여야 한다 — `play()` 가 거부됐는데 참으로 두면 다음 복귀가 조용히 실패한다.
 */
let unlocked = false;

export function markBuffersUnlocked(): void {
  unlocked = true;
}

export function buffersUnlocked(): boolean {
  return unlocked;
}
